// Assets View - 3D Asset Gallery
import { getAssets, createAsset, deleteAsset, updateSite, getSite, createFolder, getFolders, deleteFolder, updateAssetFolder, createPhotogrammetryJob } from '../firebase/db.js';
import { upload3DFile, isSupportedFormat, getMaxFileSize, deleteFile } from '../firebase/storage.js';
import { getCurrentUser } from '../firebase/auth.js';
import { deductCredits } from '../firebase/credits.js';
import router from '../router.js';
import { confirm } from '../components/Modal.js';
import { toast } from '../components/Toast.js';
import { Viewer3D } from '../components/Viewer3D.js';
import { t } from '../i18n.js';
import JSZip from 'jszip';


let viewer3d = null;

/**
 * Create assets view
 * @param {Object} site 
 * @param {Array} assets 
 * @returns {string} HTML string
 */
/**
 * Create assets view
 * @param {Object} site 
 * @param {Array} assets 
 * @param {Array} folders
 * @param {string|null} currentFolderId
 * @returns {string} HTML string
 */
export function createAssetsView(site, assets = [], folders = [], currentFolderId = null) {
  if (!site) {
    return `
      <div class="page">
        <div class="container">
          <div class="empty-state">
            <div class="empty-state-icon">❌</div>
            <h2 class="empty-state-title">${t('app.error')}</h2>
            <a href="#/dashboard" class="btn btn-primary">${t('nav.dashboard')}</a>
          </div>
        </div>
      </div>
    `;
  }

  const currentUser = getCurrentUser();
  const isOwner = currentUser && site.userId === currentUser.uid;
  const perms = isOwner ?
    { canUploadAsset: true, canDeleteAsset: true } :
    (site.permissions?.[currentUser?.email] || { canUploadAsset: false, canDeleteAsset: false });

  // Filter assets by current folder
  const currentAssets = assets.filter(a => (a.folderId || null) === currentFolderId);
  const currentFolder = folders.find(f => f.id === currentFolderId);

  return `
    <div class="page">
      <div class="container">
        <!-- Breadcrumb -->
        <div class="breadcrumb">
          <a href="#/dashboard">${t('nav.dashboard')}</a>
          <span class="breadcrumb-sep">›</span>
          <a href="#/site/${site.id}">${escapeHtml(site.name)}</a>
          <span class="breadcrumb-sep">›</span>
          <a href="#" class="folder-link" data-folder-id="">${t('site.tabAssets')}</a>
          ${currentFolder ? `
            <span class="breadcrumb-sep">›</span>
            <span>📂 ${escapeHtml(currentFolder.name)}</span>
          ` : ''}
        </div>
        
        <div class="page-header flex justify-between items-center flex-mobile-column">
          <div>
            <h1 class="page-title">🗿 ${t('site.tabAssets')}</h1>
          </div>
          <div class="flex gap-2 w-full mobile-btns">
            <button class="btn btn-secondary btn-sm flex-1" id="create-folder-btn">
              📂 ${t('assets.createFolder')}
            </button>
            <button class="btn btn-secondary btn-sm flex-1" id="guide-btn">
               ℹ️ ${t('assets.guide')}
            </button>
          </div>
        </div>
        
        <!-- Upload Area -->
        ${perms.canUploadAsset ? `
        <div class="dropzone" id="upload-dropzone">
          <div class="dropzone-icon">📤</div>
          <div class="dropzone-text">${t('assets.dragDrop')}</div>
          <div class="dropzone-hint">${t('assets.supported')}</div>
          <input type="file" id="file-input" accept=".obj,.stl,.ply,.gltf,.glb,.jpg,.jpeg,.png,.webp" multiple hidden>
          <input type="file" id="camera-input" accept="image/*" capture="environment" hidden>
          <div class="flex gap-2 mt-4">
            <button class="btn btn-secondary" id="browse-btn">${t('assets.browse')}</button>
            <button class="btn btn-primary mobile-only" id="take-photo-btn">📸 ${t('assets.takePhoto')}</button>
          </div>
        </div>` : ''}
        
        <!-- Upload Progress -->
        <div class="upload-progress hidden" id="upload-progress">
          <div class="upload-info">
            <span id="upload-filename">Yükleniyor...</span>
            <span id="upload-percent">0%</span>
          </div>
          <div class="progress">
            <div class="progress-bar" id="progress-bar" style="width: 0%"></div>
          </div>
        </div>
        
        <!-- Content Grid -->
        ${(folders.length > 0 || currentAssets.length > 0) ? `
          <h2 class="mt-8 mb-4">
            ${currentFolder ? `📂 ${escapeHtml(currentFolder.name)}` : t('assets.title')} 
            <span class="text-sm text-muted font-normal">(${currentFolderId === null ? folders.length + ` ${t('assets.folderCount')}, ` : ''}${currentAssets.length} ${t('assets.fileCount')})</span>
          </h2>
          
          <div class="asset-grid" id="asset-grid">
            <!-- Folders (Only show in root or if we implement nested later) -->
            ${currentFolderId === null ? folders.map(folder => createFolderCard(folder, perms)).join('') : ''}
            
            <!-- Assets -->
            ${currentAssets.map(asset => createAssetCard(asset, perms)).join('')}
          </div>
        ` : `
          <div class="empty-state mt-8">
            <div class="empty-state-icon">🗿</div>
            <h2 class="empty-state-title">${t('assets.empty')}</h2>
            <p class="empty-state-text">${t('assets.dragDrop')} ${t('assets.browse')}</p>
          </div>
        `}
        
        <!-- 3D Viewer Modal -->
        <div class="modal-backdrop" id="viewer-modal">
          <div class="modal viewer-modal-inner">
            <div class="modal-header">
              <h3 class="modal-title" id="viewer-title">${t('assets.viewerTitle')}</h3>
              <button class="modal-close" data-modal-close aria-label="${t('app.close')}">✕</button>
            </div>
            <div class="modal-body" style="padding: 0; position: relative; overflow: hidden;">
              <div class="viewer-3d" id="viewer-container"></div>
              <div class="viewer-controls">
                <button class="btn btn-secondary btn-sm" id="viewer-reset">${t('assets.resetView')}</button>
                <button class="btn btn-secondary btn-sm" id="viewer-rotate">${t('assets.rotation')}</button>
                <button class="btn btn-primary btn-sm hidden" id="viewer-pdf">${t('assets.pdfBtn')}</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Guide Modal -->
        ${createModal('guide-modal', '3D Model Oluşturma Rehberi (3DF Zephyr Free)', createGuideModalContent(), { size: 'large', showFooter: false })}

        <!-- Create Folder Modal -->
        ${createModal('create-folder-modal', t('assets.createFolder'), `
          <div class="form-group">
            <label class="form-label">${t('assets.folderName')}</label>
            <input type="text" id="new-folder-name" class="form-input" placeholder="Örn: Seramikler">
          </div>
        `, { confirmText: t('app.save') })}

        <!-- Move Asset Modal -->
        ${createModal('move-asset-modal', t('assets.moveAsset'), `
          <div class="form-group">
            <label class="form-label">${t('assets.targetFolder')}</label>
            <select id="move-target-folder" class="form-select">
              <option value="">${t('assets.rootDir')}</option>
              ${folders.map(f => `<option value="${f.id}">📂 ${escapeHtml(f.name)}</option>`).join('')}
            </select>
          </div>
        `, { confirmText: t('assets.move') })}

      </div>
    </div>
    
    <style>
      .breadcrumb {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-bottom: var(--space-6);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }
      
      .breadcrumb a {
        color: var(--color-text-secondary);
        text-decoration: none;
      }
      .breadcrumb a:hover {
        text-decoration: underline;
      }
      
      .upload-progress {
        margin-top: var(--space-4);
        padding: var(--space-4);
        background: var(--color-bg-secondary);
        border-radius: var(--radius-lg);
      }
      
      .upload-progress.hidden {
        display: none;
      }
      
      .upload-info {
        display: flex;
        justify-content: space-between;
        margin-bottom: var(--space-2);
        font-size: var(--text-sm);
      }
      
      .asset-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
        gap: var(--space-5);
      }
      
      .folder-card {
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border);
        border-radius: var(--radius-lg);
        padding: var(--space-4);
        cursor: pointer;
        transition: all var(--transition-base);
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        height: 100%;
        justify-content: center;
      }

      .folder-card:hover {
        border-color: var(--color-accent-primary);
        transform: translateY(-4px);
        box-shadow: var(--shadow-lg);
      }

      .folder-icon {
        font-size: 3rem;
        margin-bottom: var(--space-2);
      }

      .folder-name {
        font-weight: 500;
        margin-bottom: var(--space-2);
      }

      .folder-actions {
        opacity: 0;
        transition: opacity var(--transition-fast);
      }

      .folder-card:hover .folder-actions {
        opacity: 1;
      }

      .mobile-only {
        display: none;
      }

      @media (max-width: 768px) {
        .mobile-only {
          display: inline-flex;
        }
      }
      
      .asset-card {
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border);
        border-radius: var(--radius-lg);
        overflow: hidden;
        cursor: pointer;
        transition: all var(--transition-base);
      }
      
      .asset-card:hover {
        border-color: var(--color-accent-primary);
        transform: translateY(-4px);
        box-shadow: var(--shadow-lg), var(--shadow-glow);
      }
      
      .asset-card-preview {
        width: 100%;
        aspect-ratio: 1;
        background: linear-gradient(135deg, var(--color-bg-tertiary), var(--color-bg-elevated));
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 4rem;
      }
      
      .asset-card-info {
        padding: var(--space-4);
      }
      
      .asset-card-name {
        font-size: var(--text-sm);
        font-weight: 500;
        margin-bottom: var(--space-1);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .asset-card-id {
        font-size: var(--text-xs);
        color: var(--color-accent-primary);
        font-family: monospace;
        margin-bottom: var(--space-2);
      }
      
      .asset-card-meta {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      
      .asset-card-format {
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        text-transform: uppercase;
      }
      
      .asset-action-btn {
        background: none;
        border: none;
        color: var(--color-text-tertiary);
        cursor: pointer;
        padding: var(--space-1);
        border-radius: var(--radius-sm);
        transition: all var(--transition-fast);
        margin-left: 4px;
      }
      
      .asset-action-btn:hover {
        background: var(--color-bg-tertiary);
        color: var(--color-text-primary);
      }

      .asset-action-btn.delete:hover {
        background: rgba(248, 113, 113, 0.15);
        color: var(--color-error);
      }
      
      .viewer-3d {
        position: relative;
        width: 100%;
        height: 70vh;
        background: radial-gradient(circle at center, #1b1b1e 0%, #0d0d0f 100%);
      }

      .viewer-modal-inner {
        max-width: 1000px;
        width: 95%;
      }
      
      .viewer-controls {
        position: absolute;
        bottom: var(--space-4);
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        gap: var(--space-2);
        padding: var(--space-2);
        background: rgba(13, 13, 15, 0.8);
        backdrop-filter: blur(8px);
        border-radius: var(--radius-lg);
        border: 1px solid var(--color-border);
        z-index: 10;
      }

      @media (max-width: 768px) {
        .viewer-3d {
          height: 60vh;
        }
        .viewer-modal-inner {
          width: 100%;
          height: 100%;
          max-width: none;
          border-radius: 0;
        }
        .asset-grid {
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
        }
        .mobile-btns {
           margin-top: var(--space-4);
        }
      }

      .guide-steps {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
      }
      .guide-step {
        background: var(--color-bg-secondary);
        padding: 1rem;
        border-radius: var(--radius-lg);
        border-left: 3px solid var(--color-accent-primary);
      }
      .guide-step h4 {
        margin: 0 0 0.5rem 0;
        color: var(--color-text-primary);
      }
      .guide-step p {
        margin: 0;
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
        line-height: 1.5;
      }
      .guide-step ul {
        margin: 0.5rem 0 0 1.2rem;
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
      }
    </style>
  `;
}

/**
 * Create folder card HTML
 */
function createFolderCard(folder, perms) {
  return `
      <div class="folder-card" data-folder-id="${folder.id}">
        <div class="folder-icon">📂</div>
        <div class="folder-name">${escapeHtml(folder.name)}</div>
        <div class="folder-actions">
          <button class="asset-action-btn" title="${t('recon.photogrammetryTitle')}" data-generate-3d="${folder.id}" data-folder-name="${escapeHtml(folder.name)}">✨ 3D</button>
          ${perms.canDeleteAsset ? `<button class="asset-action-btn delete" title="${t('app.delete')}" data-delete-folder="${folder.id}">🗑️</button>` : ''}
        </div>
      </div>
    `;
}

/**
 * Create asset card HTML
 * @param {Object} asset 
 * @param {Object} perms
 * @returns {string}
 */
function createAssetCard(asset, perms) {
  const formatIcons = {
    obj: '📦',
    stl: '🔷',
    ply: '🌐',
    gltf: '🎨',
    glb: '🎨'
  };

  const artifactId = asset.artifactId !== undefined ? `#${asset.artifactId}` : '';

  return `
    <div class="asset-card" data-asset-id="${asset.id}" data-url="${asset.url}" data-format="${asset.format}" data-pdf-url="${asset.pdfUrl || ''}">
      <div class="asset-card-preview">
        ${formatIcons[asset.format] || '🗿'}
      </div>
      <div class="asset-card-info">
        <div class="asset-card-name" title="${escapeHtml(asset.name)}">${escapeHtml(asset.name)}</div>
        ${artifactId ? `<div class="asset-card-id">${artifactId}</div>` : ''}
        <div class="asset-card-meta">
          <span class="asset-card-format">${asset.format}</span>
          <div class="flex">
            <button class="asset-action-btn" title="Taşı" data-move="${asset.id}">📂</button>
            ${perms.canDeleteAsset ? `<button class="asset-action-btn delete" title="Sil" data-delete="${asset.id}">🗑️</button>` : ''}
          </div>
        </div>
      </div>
    </div>
  `;
}

/**
 * Initialize assets view
 * @param {Object} site 
 * @param {Array} assets 
 * @param {Array} folders
 */
export function initAssetsView(site, assets = [], folders = [], currentUser = null) {
  if (!site) return;

  initModals();

  const dropzone = document.getElementById('upload-dropzone');
  const fileInput = document.getElementById('file-input');
  const cameraInput = document.getElementById('camera-input');
  const browseBtn = document.getElementById('browse-btn');
  const uploadProgress = document.getElementById('upload-progress');

  // Guide Button
  document.getElementById('guide-btn')?.addEventListener('click', () => {
    showModal('guide-modal');
  });

  // Browse & Camera buttons
  browseBtn?.addEventListener('click', () => fileInput.click());
  
  const takePhotoBtn = document.getElementById('take-photo-btn');
  takePhotoBtn?.addEventListener('click', () => {
    cameraInput?.click();
  });

  // File input change
  cameraInput?.addEventListener('change', (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleFiles(files, site);
      // Clear for next time
      cameraInput.value = '';
    }
  });
  fileInput?.addEventListener('change', (e) => {
    handleFiles(e.target.files, site);
  });

  // Drag and drop
  dropzone?.addEventListener('dragover', (e) => {
    e.preventDefault();
    dropzone.classList.add('dragover');
  });

  dropzone?.addEventListener('dragleave', () => {
    dropzone.classList.remove('dragover');
  });

  dropzone?.addEventListener('drop', (e) => {
    e.preventDefault();
    dropzone.classList.remove('dragover');
    handleFiles(e.dataTransfer.files, site);
  });

  // Create Folder Button
  document.getElementById('create-folder-btn')?.addEventListener('click', () => {
    showModal('create-folder-modal');
  });

  // Create Folder Confirm
  document.querySelector('#create-folder-modal [data-modal-confirm]')?.addEventListener('click', async () => {
    const nameInput = document.getElementById('new-folder-name');
    const name = nameInput.value.trim();
    if (name) {
      await createFolder(site.id, name);
      toast.success('Klasör oluşturuldu');
      router.handleRoute(); // Refresh
    }
  });

  // Folder navigation
  document.querySelectorAll('.folder-card').forEach(card => {
    card.addEventListener('click', (e) => {
      // Ignore if clicking action buttons
      if (e.target.closest('.asset-action-btn')) return;

      const folderId = card.dataset.folderId;
      // We need to re-render with this folder ID.
      // Since our router handles main views, we might need a way to pass state.
      // For now, let's use a query param or simple re-render approach if supported.
      // Or we can just re-call render with new state if we have access.
      // A simple way is to use hash params for folders? e.g. #/site/:id/assets?folder=xyz
      // For simplicity in this codebase, let's dispatch a custom event or use the router if it supports query params.
      // Assuming router can handle it or we updated it. 
      // WITHOUT modifying router heavily: We can manually call initAssetsView with new folder?
      // But we need the view to re-render. 
      // Let's use window.location.hash update if we update router to support query params,
      // OR we can store state in a global or closure if `createAssetsView` is called by router.

      // OPTION: We'll modify the router check to parse query params (in a future step if needed),
      // BUT for now, let's assume we can trigger a route update with a query param.
      // If router doesn't support it, we might need to modify router.js.
      // Let's check router usage. It usually handles #/path.
      // Let's try adding a query param to the hash.
      window.location.hash = `#/site/${site.id}/assets?folder=${folderId}`;
    });
  });

  // Folder Link (Breadcrumb)
  document.querySelectorAll('.folder-link').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const folderId = link.dataset.folderId; // Empty for root
      if (folderId) {
        window.location.hash = `#/site/${site.id}/assets?folder=${folderId}`;
      } else {
        window.location.hash = `#/site/${site.id}/assets`;
      }
    });
  });

  // Delete Folder
  document.querySelectorAll('[data-delete-folder]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const folderId = btn.dataset.deleteFolder;
      if (await confirm('Klasörü Sil', 'Bu klasörü ve içeriğini silmek istediğinize emin misiniz? (İçerik ana dizine taşınacak)')) {
        await deleteFolder(site.id, folderId);
        toast.success('Klasör silindi');
        router.handleRoute();
      }
    });
  });

  // Move Asset Button
  let assetToMove = null;
  document.querySelectorAll('[data-move]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      assetToMove = btn.dataset.move;

      // We need to re-render the select options here or simply reuse the static one from createView.
      // Since folders are passed to initAssetsView now, we can dynamically populate if needed, 
      // but simpler is to trust the statically rendered modal if folders don't change without reload.
      // However, the modal HTML is already rendered with folders.

      showModal('move-asset-modal');
    });
  });

  // Confirm Move
  document.querySelector('#move-asset-modal [data-modal-confirm]')?.addEventListener('click', async () => {
    const targetFolder = document.getElementById('move-target-folder').value || null;
    if (assetToMove) {
      await updateAssetFolder(site.id, assetToMove, targetFolder);
      toast.success('Varlık taşındı');
      router.handleRoute();
    }
  });


  // Asset card clicks - open viewer (Modify to exclude move button)
  document.querySelectorAll('.asset-card').forEach(card => {
    card.addEventListener('click', (e) => {
      // Don't open if clicking actions
      if (e.target.closest('.asset-action-btn') || e.target.closest('.asset-card-delete')) return;

      const url = card.dataset.url;
      const format = card.dataset.format;
      const name = card.querySelector('.asset-card-name').textContent;
      const pdfUrl = card.dataset.pdfUrl || '';

      openViewer(url, format, name, pdfUrl);
    });
  });

  // Delete buttons
  document.querySelectorAll('[data-delete]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const assetId = btn.dataset.delete;
      const asset = assets.find(a => a.id === assetId);

      const confirmed = await confirm(
        'Varlığı Sil',
        `"${asset?.name || ''}" adlı varlığı silmek istediğinizden emin misiniz?`
      );

      if (confirmed) {
        await handleDeleteAsset(site.id, assetId, asset?.path);
      }
    });
  });

  // Viewer modal controls
  document.getElementById('viewer-reset')?.addEventListener('click', () => {
    if (viewer3d) viewer3d.resetCamera();
  });

  let autoRotate = false;
  document.getElementById('viewer-rotate')?.addEventListener('click', () => {
    autoRotate = !autoRotate;
    if (viewer3d) viewer3d.setAutoRotate(autoRotate);
  });

  // Generate 3D Model Button
  document.querySelectorAll('[data-generate-3d]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const folderId = btn.getAttribute('data-generate-3d');
      const folderName = btn.getAttribute('data-folder-name');

      // Count photos in this folder
      const folderPhotos = assets.filter(a => {
        if (String(a.folderId || '').trim() !== String(folderId || '').trim()) return false;
        
        const nameStr = String(a.name || '').toLowerCase().trim();
        const formatStr = String(a.format || '').toLowerCase().trim();
        const typeStr = String(a.type || '').toLowerCase().trim();

        const imgExts = ['jpg', 'jpeg', 'png', 'webp'];
        
        const nameMatch = imgExts.some(ext => nameStr.includes(ext));
        const formatMatch = imgExts.some(ext => formatStr.includes(ext));
        const typeMatch = typeStr.includes('image');
        
        return nameMatch || formatMatch || typeMatch;
      });

      const count = folderPhotos.length;
      if (count < 10) {
        toast.error(`${t('recon.photogrammetryMinPhotos')} (Bulunan: ${count})`);
        return;
      }

      // Fixed cost: 2 credits for any model creation
      const creditCost = 2;

      const message = `
        <p>${t('recon.photogrammetryConfirm').replace('{name}', folderName).replace('{count}', count).replace('{credits}', creditCost)}</p>
        <p style="margin-top: 10px; font-size: 0.9em; color: var(--text-secondary);">${t('recon.photogrammetryDesc')}</p>
      `;

      if (await confirm(t('recon.photogrammetryTitle'), message)) {
        try {
          // Deduct credits
          const deduction = await deductCredits(currentUser.uid, creditCost, `${folderName} - 3D model creation (${count} photos)`);
          
          if (!deduction.success) {
            toast.error(t('credits.insufficient'));
            return;
          }

          toast.info(t('recon.uploadingModel').replace('Model', 'Photos'));
          const formData = new FormData();
          for (const photo of folderPhotos) {
              const res = await fetch(photo.url);
              const blob = await res.blob();
              let fName = photo.name || 'image.jpg';
              if (!fName.includes('.')) {
                  fName += '.' + (photo.format || 'jpg');
              }
              formData.append('files', blob, fName);
          }

          toast.info(t('recon.processingTitle') + " (Modal API)");
          const submitRes = await fetch('https://eri-meri11--archaeohub-model-generator-web.modal.run/v1/models', {
              method: 'POST',
              headers: { 'accept': 'application/json' },
              body: formData
          });

          if (!submitRes.ok) throw new Error("Upload failed");
          const submitData = await submitRes.json();
          const jobId = submitData.job_id;

          let isComplete = false;
          while (!isComplete) {
              await new Promise(resolve => setTimeout(resolve, 10000));
              const statusRes = await fetch(`https://eri-meri11--archaeohub-model-generator-web.modal.run/v1/models/${jobId}`, {
                  headers: { 'accept': 'application/json' }
              });
              if (!statusRes.ok) throw new Error("Status check failed");
              const statusData = await statusRes.json();
              
              if (statusData.status === 'completed' || statusData.status === 'success') {
                  isComplete = true;
              } else if (statusData.status === 'failed' || statusData.status === 'error') {
                  throw new Error("Job failed on server");
              }
          }

          toast.info(t('recon.packagingLib'));
          const downloadRes = await fetch(`https://eri-meri11--archaeohub-model-generator-web.modal.run/v1/models/${jobId}/download`);
          if (!downloadRes.ok) throw new Error("Download failed");
          const zipBlob = await downloadRes.blob();

          const zip = await JSZip.loadAsync(zipBlob);
          let modelFile = null;
          
          for (const [filename, zipEntry] of Object.entries(zip.files)) {
              if (!zipEntry.dir && (filename.endsWith('.glb') || filename.endsWith('.obj'))) {
                  const content = await zipEntry.async('blob');
                  modelFile = new File([content], `${folderName}_3D${filename.endsWith('.glb') ? '.glb' : '.obj'}`, { type: filename.endsWith('.glb') ? 'model/gltf-binary' : 'text/plain' });
                  break;
              }
          }

          if (!modelFile) throw new Error("No valid model in result archive");

          toast.info(t('recon.saveToLib'));
          const uploadResult = await upload3DFile(site.id, modelFile);
          await createAsset(site.id, {
              ...uploadResult,
              folderId: folderId,
              userId: currentUser.uid,
              createdAt: Date.now()
          });

          toast.success(t('recon.done'));
          setTimeout(() => location.reload(), 2000);
        } catch (error) {
          console.error('API integration error:', error);
          toast.error(t('app.error') + ": " + error.message);
        }
      }
    });
  });

  // PDF button
  document.getElementById('viewer-pdf')?.addEventListener('click', () => {
    const pdfUrl = document.getElementById('viewer-pdf').dataset.pdfUrl;
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  });

  // Close viewer modal
  document.querySelector('#viewer-modal [data-modal-close]')?.addEventListener('click', () => {
    closeViewer();
  });

  document.getElementById('viewer-modal')?.addEventListener('click', (e) => {
    if (e.target.id === 'viewer-modal') closeViewer();
  });
}

/**
 * Handle file uploads
 */
async function handleFiles(files, site) {
  const uploadProgress = document.getElementById('upload-progress');
  const progressBar = document.getElementById('progress-bar');
  const uploadFilename = document.getElementById('upload-filename');
  const uploadPercent = document.getElementById('upload-percent');

  for (const file of Array.from(files)) {
    // Validate format
    if (!isSupportedFormat(file.name)) {
      toast.error(`Desteklenmeyen format: ${file.name}`);
      continue;
    }

    // Validate size
    if (file.size > getMaxFileSize()) {
      toast.error(`Dosya çok büyük: ${file.name} (maks 50MB)`);
      continue;
    }

    // Show progress
    uploadProgress.classList.remove('hidden');
    uploadFilename.textContent = file.name;
    uploadPercent.textContent = '0%';
    progressBar.style.width = '0%';

    try {
      const result = await upload3DFile(site.id, file, (progress) => {
        uploadPercent.textContent = `${progress}%`;
        progressBar.style.width = `${progress}%`;
      });

      // Create asset record in Firestore
      await createAsset(site.id, {
        name: result.name,
        url: result.url,
        path: result.path,
        format: result.format,
        size: result.size,
        folderId: new URLSearchParams(window.location.hash.split('?')[1]).get('folder') || null // Add to current folder
      });

      // Update site asset count
      const currentSite = await getSite(site.id);
      await updateSite(site.id, {
        assetCount: (currentSite.assetCount || 0) + 1
      });

      toast.success(`Yüklendi: ${file.name}`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Yükleme başarısız: ${file.name}`);
    }
  }

  // Hide progress and refresh
  uploadProgress.classList.add('hidden');
  router.handleRoute();
}

/**
 * Handle delete asset
 */
async function handleDeleteAsset(siteId, assetId, storagePath) {
  try {
    // Delete from storage if path exists
    if (storagePath) {
      await deleteFile(storagePath);
    }

    // Delete from Firestore
    await deleteAsset(siteId, assetId);

    // Update site asset count
    const currentSite = await getSite(siteId);
    await updateSite(siteId, {
      assetCount: Math.max(0, (currentSite.assetCount || 1) - 1)
    });

    toast.success('Varlık silindi');
    router.handleRoute();
  } catch (error) {
    console.error('Delete asset error:', error);
    toast.error('Varlık silinemedi');
  }
}

/**
 * Open 3D viewer modal
 */
function openViewer(url, format, name, pdfUrl = '') {
  const modal = document.getElementById('viewer-modal');
  const title = document.getElementById('viewer-title');
  const container = document.getElementById('viewer-container');
  const pdfBtn = document.getElementById('viewer-pdf');

  title.textContent = name;
  modal.classList.add('active');
  document.body.style.overflow = 'hidden';

  // Show/hide PDF button based on pdfUrl
  if (pdfUrl) {
    pdfBtn.classList.remove('hidden');
    pdfBtn.dataset.pdfUrl = pdfUrl;
  } else {
    pdfBtn.classList.add('hidden');
    pdfBtn.dataset.pdfUrl = '';
  }

  // Initialize viewer
  setTimeout(async () => {
    try {
      viewer3d = new Viewer3D(container);
      await viewer3d.loadModel(url, format);
    } catch (error) {
      console.error('Viewer error:', error);
      toast.error('3D model yüklenemedi');
    }
  }, 100);
}

/**
 * Close 3D viewer modal
 */
function closeViewer() {
  const modal = document.getElementById('viewer-modal');
  modal.classList.remove('active');
  document.body.style.overflow = '';

  if (viewer3d) {
    viewer3d.dispose();
    viewer3d = null;
  }

  // Clear container
  const container = document.getElementById('viewer-container');
  container.innerHTML = '';
}

/**
 * Create Guide Modal Content
 */
function createGuideModalContent() {
  return `
    <div class="guide-steps">
      <div class="guide-step">
        <h4>1. 3DF Zephyr Free İndirin</h4>
        <p>ArchaeoHub, fotogrametri için <strong>3DF Zephyr Free</strong> sürümünü önerir. Kullanımı kolay ve ücretsizdir (50 fotoğrafa kadar).</p>
        <div class="mt-2">
          <a href="https://www.3dflow.net/3df-zephyr-free-edition/" target="_blank" class="text-accent underline">Resmi Web Sitesinden İndir ↗</a>
        </div>
      </div>

      <div class="guide-step">
        <h4>2. Fotoğraf Çekimi</h4>
        <p>Kaliteli bir 3D model için doğru fotoğraf çekimi kritiktir:</p>
        <ul>
            <li>Nesnenin etrafında 360 derece dönerek çekim yapın (en az 20-30 fotoğraf).</li>
            <li>Her fotoğraf bir öncekini %60-70 oranında örtmelidir (overlap).</li>
            <li>Işıklandırma eşit olmalı, gölgelerden kaçının.</li>
            <li>Nesneyi hareket ettirmeyin, siz nesnenin etrafında dönün.</li>
        </ul>
      </div>

      <div class="guide-step">
        <h4>3. Programda İşleme</h4>
        <p>3DF Zephyr'i açın ve şu adımları izleyin:</p>
        <ul>
            <li><strong>Workflow > New Project:</strong> Fotoğraflarınızı içe aktarın.</li>
            <li><strong>Workflow > 3D Model Generation:</strong> Sihirbazı takip edin (Varsayılan ayarlar genellikle yeterlidir).</li>
            <li>İşlem tamamlandığında, modeliniz ekranda görünecektir.</li>
        </ul>
      </div>

      <div class="guide-step">
        <h4>4. Dışa Aktarma (Export)</h4>
        <p>Modeli ArchaeoHub'a yüklemek için dışa aktarın:</p>
        <ul>
            <li><strong>Export > Export Textured Mesh:</strong> Menüsünü seçin.</li>
            <li>Format olarak <strong>OBJ/MTL</strong> seçin.</li>
            <li>"Export Textures" seçeneğinin işaretli olduğundan emin olun.</li>
            <li>Dosyayı kaydedin.</li>
        </ul>
      </div>

      <div class="guide-step">
        <h4>5. Yükleme</h4>
        <p>Oluşturduğunuz <strong>.obj</strong> dosyasını bu sayfadaki yükleme alanına sürükleyin.</p>
      </div>
    </div>
  `;
}

/**
 * Escape HTML
 */
function escapeHtml(str) {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

import { createModal, initModals, showModal } from '../components/Modal.js';
